/**
 * this file is derived from https://github.com/gregnb/react-to-print/blob/master/src/index.tsx
 * the APIs for that library don't work well for ONSHOP-3718 in PaymentPlanReviewModal
 * this exposes a print dialog method for a given content's El
 */
import { findDOMNode } from "react-dom";

const removeWindow = (target: any) => {
    setTimeout(() => {
        target.parentNode.removeChild(target);
    }, 0);
};

const triggerPrint = (target: any) => {
    setTimeout(() => {
        target.contentWindow.focus();
        target.contentWindow.print();
        setTimeout(() => {
            removeWindow(target);
        }, 1000);
    }, 0);
};

export const openPrintDialog = (contentEl: HTMLElement, document: Document) => {
    const bodyClass = "";
    const copyStyles = true;

    if (!contentEl) {
        console.error(
            `Refs are not available for stateless components. For "react-to-print" to work only Class based components can be printed`
        );
        return;
    }

    const printWindow = document.createElement("iframe");
    printWindow.style.position = "absolute";
    printWindow.style.top = "-1000px";
    printWindow.style.left = "-1000px";

    const contentNodes = findDOMNode(contentEl);
    const linkNodes = document.querySelectorAll("link[rel='stylesheet']");

    const linkTotal: number = linkNodes.length || 0;
    const linksLoaded: any[] = [];
    const linksErrored: any[] = [];

    const markLoaded = (linkNode: any, loaded: any) => {
        if (loaded) {
            linksLoaded.push(linkNode);
        } else {
            console.error(
                `"react-to-print" was unable to load a link. It may be invalid. "react-to-print" will continue attempting to print the page. The link the errored was:`,
                linkNode
            );
            linksErrored.push(linkNode);
        }

        // We may have errors, but attempt to print anyways - maybe they are trivial and the user will
        // be ok ignoring them
        if (linksLoaded.length + linksErrored.length === linkTotal) {
            triggerPrint(printWindow);
        }
    };

    printWindow.onload = () => {
        /* IE11 support */
        if (window.navigator && window.navigator.userAgent.indexOf("Trident/7.0") > -1) {
            printWindow.onload = null;
        }

        const domDoc = printWindow.contentDocument || printWindow.contentWindow!.document;
        const srcCanvasEls = (contentNodes as HTMLCanvasElement).querySelectorAll("canvas");

        domDoc.open();
        domDoc.write((contentNodes as HTMLCanvasElement).outerHTML);
        domDoc.close();

        /* remove date/time from top */
        const defaultPageStyle =
            "@page { size: auto;  margin: 0mm; } @media print { body { -webkit-print-color-adjust: exact; } }";

        const styleEl = domDoc.createElement("style");
        styleEl.appendChild(domDoc.createTextNode(defaultPageStyle));
        domDoc.head.appendChild(styleEl);

        if (bodyClass.length) {
            domDoc.body.classList.add(bodyClass);
        }

        const canvasEls = domDoc.querySelectorAll("canvas");
        for (let index = 0; index < canvasEls.length; ++index) {
            const node = canvasEls[index];
            node.getContext("2d")!.drawImage(srcCanvasEls[index], 0, 0);
        }

        if (copyStyles) {
            const headEls = document.querySelectorAll("style, link[rel='stylesheet']");

            for (let index = 0; index < headEls.length; ++index) {
                const node = headEls[index];
                if (node.tagName === "STYLE") {
                    const newHeadEl = domDoc.createElement(node.tagName);
                    const sheet = (node as HTMLStyleElement).sheet as CSSStyleSheet;

                    if (sheet && sheet.cssRules) {
                        let styleCSS = "";
                        // tslint:disable-next-line
                        for (let i = 0; i < sheet.cssRules.length; i++) {
                            // IE11 requires this check or the error "Member Not Found" would be thrown
                            // tslint:disable-next-line
                            if (typeof sheet.cssRules[i].cssText === "string") {
                                styleCSS += `${sheet.cssRules[i].cssText}\r\n`;
                            }
                        }
                        newHeadEl.setAttribute("id", `react-to-print-${index}`);
                        newHeadEl.appendChild(domDoc.createTextNode(styleCSS));
                        domDoc.head.appendChild(newHeadEl);
                    }
                } else {
                    // Many browsers will do all sorts of weird things if they encounter an empty `href`
                    // tag (which is invalid HTML). Some will attempt to load the current page. Some will
                    // attempt to load the page"s parent directory. These problems can cause
                    // `react-to-print` to stop  without any error being thrown. To avoid such problems we
                    // simply do not attempt to load these links.
                    if (node.hasAttribute("href")) {
                        const newHeadEl = domDoc.createElement(node.tagName);

                        // node.attributes has NamedNodeMap type that not Array and can be iterated only via direct [i] access
                        // tslint:disable-next-line
                        for (let i = 0, l = node.attributes.length; i < l; ++i) {
                            const attr = node.attributes[i];
                            newHeadEl.setAttribute(attr.nodeName, attr.nodeValue!);
                        }

                        newHeadEl.onload = markLoaded.bind(null, newHeadEl, true);
                        newHeadEl.onerror = markLoaded.bind(null, newHeadEl, false);
                        domDoc.head.appendChild(newHeadEl);
                    } else {
                        console.warn(
                            `"react-to-print" encountered a <link> tag with an empty "href" attribute. In addition to being invalid HTML, this can cause problems in many browsers, and so the <link> was not loaded. The <link> is:`,
                            node
                        ); // eslint-disable-line no-console
                        markLoaded(node, true); // `true` because we"ve already shown a warning for this
                    }
                }
            }
        }

        if (linkTotal === 0 || !copyStyles) {
            triggerPrint(printWindow);
        }
    };

    document.body.appendChild(printWindow);
};
